// src/pages/Menu.tsx
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import ProductModal, { ModalAddPayload } from "../components/ProductModal";
import { ArrowLeft, ChevronRight, LayoutGrid, ShoppingCart } from "lucide-react";
import "../assets/styles.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { readConsumerRuntimeDeviceMode, readConsumerRuntimeTableCode } from "../runtime-device-mode";
import { readTerminalConfig } from "../terminal-theme";
import { trn, useI18n } from "../shared/i18n";
import { readMenusFromStorage, readMenusUpdatedAt } from "../menu-cache";
import { normalizeProductAllergens, type ProductAllergen } from "../allergens";
import { preloadMenuImages } from "../menu-images";
import { writeStoredCart } from "../cart-storage";
import { resolveConsumerRuntimeConnectionUuid } from "../runtime-endpoints";
import { refreshBootstrap } from "../runtime-bootstrap";

/* ========= Tipos base (relajados) ========= */
type AnyObj = Record<string, any>;
interface Product {
  id: string | number;
  name: string;
  price: number;
  largeImage?: string;
  image?: string;
  shortDescription?: string;
  allergens?: ProductAllergen[];
  productOptionCategories?: any[];
  connectionId?: string | number;
  locationId?: string | number;
}
interface MenuSection {
  id: string | number;
  name: string;
  image?: string;
  shortDescription?: string;
  products: Product[];
}
interface Menu {
  id: string | number;
  name: string;
  menuSections: MenuSection[];
}
interface CartItem extends Product {
  quantity: number;
  comment: string;
  note?: string;
  options?: any[];
  productId?: string | number;
  type: "product";
  menuSectionId: number;
  connectionId?: string | number;
  locationId?: string | number;
}
interface MenuProps {
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
}

const MENU_RECONNECT_COOLDOWN_MS = 30 * 1000;

/* ========= Normalizaciones ========= */
function toNum(n: any): number {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}
function normalizeProduct(p: AnyObj): Product {
  return {
    id: p.id ?? p.uuid ?? p.urlKey ?? crypto.randomUUID(),
    name: String(p.name ?? p.label ?? trn("app.consumer.common.product", "Product")),
    price: toNum(p.price ?? p.amount ?? p.unitPrice),
    largeImage: p.largeImage ?? p.image ?? null,
    image: p.image ?? p.largeImage ?? null,
    shortDescription: p.shortDescription ?? p.description ?? null,
    allergens: normalizeProductAllergens(
      p.allergens ?? p.allergenIds ?? p.allergen_codes ?? p.allergenCodes ?? [],
      p.activeLang ?? "es"
    ),
    connectionId: p.connectionId ?? p.connection_id ?? p.connectionUuid ?? undefined,
    locationId: p.locationId ?? p.location_id ?? undefined,
    productOptionCategories:
      p.productOptionCategories ??
      p.productOptionsCategories ??
      p.options ??
      [],
  };
}
function normalizeSection(s: AnyObj): MenuSection {
  const rawProducts =
    s.products ??
    s.items ??
    s.productsList ??
    []; // varios nombres posibles
  return {
    id: s.id ?? s.uuid ?? crypto.randomUUID(),
    name: String(s.name ?? s.label ?? trn("app.consumer.common.section", "Section")),
    image: String(
      s.image ??
      s.imageUrl ??
      s.image_url ??
      s.bannerImage ??
      s.coverImageUrl ??
      s.thumbnailImage ??
      ""
    ).trim() || undefined,
    shortDescription: String(s.shortDescription ?? s.description ?? "").trim() || undefined,
    products: (rawProducts || []).map(normalizeProduct),
  };
}

function resolveSectionDisplayImage(
  section: MenuSection,
  imageSource: "category" | "first-product"
) {
  if (imageSource === "first-product") {
    const firstProductWithImage = (section.products || []).find((product) => product.image || product.largeImage);
    return firstProductWithImage?.image || firstProductWithImage?.largeImage || section.image;
  }
  return section.image;
}
function normalizeMenu(m: AnyObj): Menu {
  // Acepta tanto "sections" como "menuSections"
  const rawSections =
    m.menuSections ??
    m.sections ??
    m.menu_sections ??
    [];
  return {
    id: m.id ?? m.uuid ?? "menu",
    name: String(m.name ?? m.label ?? trn("app.consumer.common.menu", "Menu")),
    menuSections: (rawSections || []).map(normalizeSection),
  };
}
function normalizeMenus(raw: any): Menu[] {
  try {
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(data)) return data.map(normalizeMenu);
    if (data?.menus && Array.isArray(data.menus)) return data.menus.map(normalizeMenu);

    // Compact JSON con shape { sections: [...] }
    if (data && typeof data === "object") {
      if (Array.isArray((data as AnyObj).menuSections) || Array.isArray((data as AnyObj).sections)) {
        return [normalizeMenu(data as AnyObj)];
      }
    }
  } catch (e) {
    console.warn("[menu] normalizeMenus error:", e);
  }
  return [];
}

/* ========= Componente ========= */
const MenuPage: React.FC<MenuProps> = ({ cart, setCart }) => {
  const { language } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const orderType = params.get("type"); // "takeaway" | "eat-in"
  const deviceMode = readConsumerRuntimeDeviceMode();
  const tableCode = params.get("table") || readConsumerRuntimeTableCode();

  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [selectedMenuSectionId, setSelectedMenuSectionId] = useState<number | null>(null);
  const [comment, setComment] = useState<string>(""); // compat

  const [activeSection, setActiveSection] = useState<string>("categories");
  const [footerHighlight, setFooterHighlight] = useState<boolean>(false);
  const [lastAddedProduct, setLastAddedProduct] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);

  const restaurantName = localStorage.getItem("restaurantName") || trn("app.consumer.common.restaurant", "Restaurant");
  const terminalConfig = readTerminalConfig();
  const categoryTileImageSource =
    terminalConfig?.consumerRuntime?.layout?.categoryTileImageSource === "first-product"
      ? "first-product"
      : "category";
  const flattenedSections = menus.flatMap((menu, mi) =>
    (menu.menuSections || []).map((section, si) => ({
      key: `section-${mi}-${si}-${section.id}`,
      id: section.id,
      name: section.name,
      image: resolveSectionDisplayImage(section, categoryTileImageSource),
      shortDescription: section.shortDescription,
      products: section.products || [],
    }))
  );

  /* Guards + caché inmediata + revalidación automática */
  useEffect(() => {
  const hasTerminal = !!localStorage.getItem("connectionUuid");
  if (!hasTerminal) { navigate("/setup", { replace: true }); return; }
  if (deviceMode === "table") {
    if (!tableCode) { navigate("/setup", { replace: true }); return; }
    if (orderType !== "eat-in") { navigate(`/menu?type=eat-in&table=${encodeURIComponent(tableCode)}`, { replace: true }); return; }
  } else if (orderType !== "takeaway" && orderType !== "eat-in") {
    navigate("/order-type", { replace: true }); return;
  }

  let unmounted = false;
  let refreshInFlight: Promise<void> | null = null;

  const renderStoredMenus = async (showLoading: boolean) => {
    if (unmounted) return;
    if (showLoading) setLoading(true);
    const rawMenus = readMenusFromStorage(language || "es");
    const m = normalizeMenus(rawMenus);
    await preloadMenuImages(m);
    if (unmounted) return;
    requestAnimationFrame(() => {
      if (unmounted) return;
      setMenus(m);
      if (m.length) {
        setLoading(false);
        setError(null);
      } else if (showLoading) {
        setError(null);
      }
    });
  };

  const refreshMenus = (reason: string) => {
    if (refreshInFlight) return refreshInFlight;
    const connectionUuid = resolveConsumerRuntimeConnectionUuid();
    if (!connectionUuid || !navigator.onLine) return Promise.resolve();

    refreshInFlight = refreshBootstrap(connectionUuid, {
      language: language || "es",
      refreshPlatformBranding: false,
    })
      .then(async ({ normalizedMenus }) => {
        if (unmounted) return;
        const freshMenus = normalizeMenus(normalizedMenus);
        if (!freshMenus.length) return;
        await preloadMenuImages(freshMenus);
        if (unmounted) return;
        setMenus(freshMenus);
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        // La carta cacheada sigue siendo operativa si la conexión falla.
        console.warn(`[menu] refresh failed (${reason}):`, err);
        if (!unmounted && !normalizeMenus(readMenusFromStorage(language || "es")).length) {
          setLoading(false);
          setError(trn("app.consumer.menu.no_menus", "No menus available."));
        }
      })
      .finally(() => {
        refreshInFlight = null;
      });

    return refreshInFlight;
  };

  // Mostramos la caché sin bloquear y siempre revalidamos al entrar en la carta.
  const initialTimer = setTimeout(() => {
    void renderStoredMenus(true).then(() => refreshMenus("menu-entry"));
  }, 0);

  const onOnline = () => {
    const age = Date.now() - readMenusUpdatedAt(language || "es");
    if (age >= MENU_RECONNECT_COOLDOWN_MS) void refreshMenus("online");
  };

  // Cuando App emite "menus:updated", también diferimos
  const onUpdated = (event: Event) => {
    const detail = (event as CustomEvent<{ language?: string }>).detail;
    if (detail?.language && detail.language !== (language || "es")) return;
    setTimeout(() => {
      void renderStoredMenus(false);
    }, 0);
  };
  window.addEventListener("menus:updated" as any, onUpdated);
  window.addEventListener("online", onOnline);

  return () => {
    unmounted = true;
    clearTimeout(initialTimer);
    window.removeEventListener("menus:updated" as any, onUpdated);
    window.removeEventListener("online", onOnline);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [orderType, navigate, deviceMode, tableCode, language]);

  /* ==== Interacciones ==== */
  const openModalOrAddToCart = (item: Product, menuSectionId: number) => {
    const hasOptions = Array.isArray(item.productOptionCategories) && item.productOptionCategories.length > 0;
    if (hasOptions) {
      setSelectedItem(item);
      setComment("");
      setSelectedMenuSectionId(menuSectionId);
    } else {
      addToCartDirectly(item, menuSectionId);
    }
  };

  const addToCartDirectly = (item: Product, menuSectionId: number) => {
    setCart((prev) => {
      const updated: CartItem[] = prev.map((ci) =>
        String(ci.id) === String(item.id) && ci.menuSectionId === menuSectionId
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci
      );
      if (!updated.some((ci) => String(ci.id) === String(item.id) && ci.menuSectionId === menuSectionId)) {
        updated.push({
          ...item,
          productId: item.id,
          price: toNum(item.price),
          quantity: 1,
          comment: "",
          note: "",
          options: [],
          type: "product",
          menuSectionId,
          connectionId: item.connectionId,
          locationId: item.locationId,
        } as CartItem);
      }
      writeStoredCart(updated);
      return updated;
    });
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLastAddedProduct(item.name);
    setFooterHighlight(true);
    timeoutRef.current = setTimeout(() => {
      setFooterHighlight(false);
      setLastAddedProduct(null);
    }, 1200);
  };

  function addFromModal(payload: ModalAddPayload) {
    if (selectedMenuSectionId == null || !selectedItem) return;

    setCart((prev) => {
      const line: CartItem = {
        id: String(payload.productId),
        productId: String(payload.productId),
        name: payload.name,
        price: Number(payload.unitPrice), // unitario con extras incluidos
        largeImage: selectedItem.largeImage ?? selectedItem.image ?? undefined,
        quantity: payload.qty,
        comment: payload.note || "",
        note: payload.note || "",
        options: payload.options.map((option) => ({
          id: option.optionId,
          optionId: option.optionId,
          optionName: option.optionName,
          qty: option.qty,
          unitPrice: option.unitPrice,
        })),
        type: "product",
        menuSectionId: selectedMenuSectionId!,
        connectionId: selectedItem.connectionId,
        locationId: selectedItem.locationId,
      } as any;

      const updated = [...prev, line];
      writeStoredCart(updated);
      return updated;
    });

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLastAddedProduct(payload.name);
    setFooterHighlight(true);
    timeoutRef.current = setTimeout(() => {
      setFooterHighlight(false);
      setLastAddedProduct(null);
    }, 1200);

    setSelectedItem(null);
    setSelectedMenuSectionId(null);
    setComment("");
  }

  const closeModal = () => {
    setSelectedItem(null);
    setSelectedMenuSectionId(null);
    setComment("");
  };

  const changeSection = (sectionId: string) => {
    setActiveSection(sectionId);
    requestAnimationFrame(() => {
      menuContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
      window.scrollTo({ top: 0, behavior: "auto" });
    });
  };

  /* ========= Render ========= */
  return (
    <div className="fullscreen-container-dark">
      {/* Header */}
      <div className="menu-header">
        <button
          className="btn btn-secondary back-btn"
          onClick={() => navigate(deviceMode === "table" ? "/" : "/order-type")}
        >
          <ArrowLeft size={24} />
        </button>

        {/* Tabs de categorías */}
        <div ref={categoryScrollRef} className="category-scroll">
          <button
            className={`category-button category-button--all ${activeSection === "categories" ? "active" : ""}`}
            onClick={() => changeSection("categories")}
            aria-label={trn("app.consumer.menu.all_categories", "Categories")}
            title={trn("app.consumer.menu.all_categories", "Categories")}
          >
            <LayoutGrid size={18} strokeWidth={2.2} aria-hidden="true" />
            <span>{trn("app.consumer.menu.all_categories", "Categories")}</span>
          </button>

          {flattenedSections
            .map(({ key, id, name }) => (
              <button
                key={key}
                className={`category-button ${activeSection === String(id) ? "active" : ""}`}
                onClick={() => changeSection(String(id))}
              >
                {name}
              </button>
            ))}
        </div>
      </div>

      {/* Loader / Error */}
      {loading && (
        <div className="text-center text-white mt-5 display-4 fw-bold">
          {trn("app.consumer.menu.loading", "Loading menus...")}
        </div>
      )}
      {error && !loading && <div className="text-center text-danger mt-5 display-6 fw-bold">{error}</div>}

      {/* Listado */}
      {!loading && !error && (
        <div ref={menuContentRef} className="menu-content">
          {deviceMode === "table" ? (
            <div className="text-center text-white mb-4">
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 16px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.08)",
                  fontWeight: 700,
                }}
              >
                <i className="fas fa-chair" />
                {trn("app.consumer.menu.table_badge", "Table {tableCode}", { tableCode: tableCode || "?" })}
              </div>
            </div>
          ) : null}
          <div className={activeSection === "categories" ? "category-list" : "product-list"}>
            {activeSection === "categories"
              ? flattenedSections.map((section) => (
                  <button
                    key={`category-${section.key}`}
                    type="button"
                    className="category-tile"
                    onClick={() => changeSection(String(section.id))}
                  >
                    {section.image ? (
                      <div
                        className="category-tile__media"
                        style={{ backgroundImage: `url(${section.image})` }}
                      >
                        <div
                          className="category-tile__meta"
                          aria-label={trn("app.consumer.menu.products_count", "{count} products", {
                            count: section.products.length,
                          })}
                        >
                          {section.products.length}
                        </div>
                      </div>
                    ) : (
                      <div className="category-tile__badge">
                        <LayoutGrid size={22} strokeWidth={2.4} aria-hidden="true" />
                        <div
                          className="category-tile__meta"
                          aria-label={trn("app.consumer.menu.products_count", "{count} products", {
                            count: section.products.length,
                          })}
                        >
                          {section.products.length}
                        </div>
                      </div>
                    )}
                    <div className="category-tile__body">
                      <div className="category-tile__title-row">
                        <div className="category-tile__title">{section.name}</div>
                        <div
                          className="category-tile__cta"
                          aria-label={trn("app.consumer.menu.view_category", "View category")}
                          title={trn("app.consumer.menu.view_category", "View category")}
                        >
                          <ChevronRight size={18} />
                        </div>
                      </div>
                      {section.shortDescription ? (
                        <div className="category-tile__summary">{section.shortDescription}</div>
                      ) : null}
                    </div>
                  </button>
                ))
              : flattenedSections
                  .filter((section) => String(section.id) === activeSection)
                  .flatMap((section) =>
                    section.products.map((item) => (
                      <ProductCard
                        key={`prod-${section.key}-${item.id}`}
                        item={item}
                        sectionId={Number(section.id)}
                        openModalOrAddToCart={openModalOrAddToCart}
                      />
                    ))
                  )}
          </div>
        </div>
      )}

      {/* Modal */}
      {selectedItem && (
        <ProductModal
          product={selectedItem as any}
          onClose={closeModal}
          onAdd={addFromModal}
        />
      )}

      {/* Footer */}
      <div
        onClick={() => navigate("/cart", { state: { returnToMenuSearch: location.search } })}
        className={`footer ${footerHighlight ? "footer-glow" : ""}`}
      >
        <div className="footer-center">
          {footerHighlight && lastAddedProduct ? (
            <h1 className="added-product-message">
              {trn("app.consumer.menu.added_product", "Added {product}", { product: lastAddedProduct })}
            </h1>
          ) : (
            <h1 className="added-product-message">{restaurantName}</h1>
          )}
        </div>

        <div className="cart-btn">
          <ShoppingCart size={26} color="currentColor" />
          <span className="cart-badge">
            {cart.reduce((total, item) => total + item.quantity, 0)} |{" "}
            {cart.reduce((acc, item) => acc + item.quantity * item.price, 0).toFixed(2)} €
          </span>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
